
import React, { useState, useEffect } from 'react';
import baseUrl from '../Config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CandidateTable = () => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${baseUrl}/candidate-enquiries`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
          setCandidates(data.data);
          setFilteredCandidates(data.data);
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredCandidates(candidates);
      return;
    }

    const filtered = candidates.filter(candidate => {
      const searchLower = searchTerm.toLowerCase();
      return (
        candidate.candidate_name.toLowerCase().includes(searchLower) ||
        candidate.gender.toLowerCase().includes(searchLower) ||
        candidate.state.toLowerCase().includes(searchLower) ||
        candidate.whatsapp.includes(searchTerm) ||
        candidate.additional_phone.includes(searchTerm) ||
        candidate.profession.toLowerCase().includes(searchLower) ||
        candidate.country.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredCandidates(filtered);
  }, [searchTerm, candidates]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleView = async (candidateId) => {
    try {
      const response = await fetch(`${baseUrl}/candidate-enquiries/${candidateId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSelectedCandidate(data.data);
        setIsViewModalOpen(true);
      } else {
        throw new Error('API returned unsuccessful response');
      }
    } catch (err) {
      console.error('Error fetching candidate details:', err);
      toast.error('Failed to fetch candidate details. Please try again.');
    }
  };

  const handleEdit = async (candidateId) => {
    try {
      const response = await fetch(`${baseUrl}/candidate-enquiries/${candidateId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSelectedCandidate(data.data);
        setEditFormData(data.data);
        setIsEditModalOpen(true);
      } else {
        throw new Error('API returned unsuccessful response');
      }
    } catch (err) {
      console.error('Error fetching candidate for edit:', err);
      toast.error('Failed to fetch candidate details. Please try again.');
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${baseUrl}/candidate-enquiries/${editFormData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editFormData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCandidates(candidates.map(c => 
          c.id === data.data.id ? data.data : c
        ));
        
        setIsEditModalOpen(false);

        toast.success('Candidate updated successfully!');
      } else {
        throw new Error(data.message || 'API returned unsuccessful response');
      }
    } catch (err) {
      console.error('Error updating candidate:', err);
      toast.error(`Failed to update candidate: ${err.message}`);
    }
  };

  const handleDelete = async (candidateId) => {
    const candidate = candidates.find(c => c.id === candidateId);
    if (window.confirm(`Are you sure you want to delete ${candidate.candidate_name}?`)) {
      try {
        const response = await fetch(`${baseUrl}/candidate-enquiries/${candidateId}`, {
          method: 'DELETE',
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
          setCandidates(candidates.filter(c => c.id !== candidateId));
          toast.success('Candidate deleted successfully!');
        } else {
          throw new Error(data.message || 'API returned unsuccessful response');
        }
      } catch (err) {
        console.error('Error deleting candidate:', err);
        toast.error(`Failed to delete candidate: ${err.message}`);
      }
    }
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${baseUrl}/candidate-enquiries`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCandidates(data.data);
        setFilteredCandidates(data.data);
        setError(null);
      } else {
        throw new Error('API returned unsuccessful response');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error refreshing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (candidateId, e) => {
    if (e.target.closest('button')) {
      return;
    }
    handleView(candidateId);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-9 md:p8 pt-24">
       <ToastContainer 
              position="top-right" 
              autoClose={3000} 
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-black mb-4 md:mb-0">Candidate Enquiries</h1>
          <div className="flex items-center gap-4">
            <div className="bg-[#4BB648] text-white px-4 py-2 rounded-full flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
              <span className="font-semibold">{filteredCandidates.length} Candidates</span>
            </div>
            <button 
              onClick={refreshData}
              className="bg-white text-[#4BB648] border border-[#4BB648] px-4 py-2 rounded-full flex items-center gap-2 hover:bg-[#4BB648] hover:text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="relative mb-6 md:mb-8">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#4BB648]" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#4BB648] focus:border-[#4BB648]"
            placeholder="Search candidates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4BB648]"></div>
            </div>
            <p className="text-gray-600">Loading candidate data...</p>
          </div>
        )}

        {error && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="flex justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-red-500 font-medium mb-2">Error loading data</p>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={refreshData}
              className="bg-[#4BB648] text-white px-4 py-2 rounded-full flex items-center gap-2 mx-auto hover:bg-green-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              <span>Try Again</span>
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#4BB648]">
                  <tr>
                    <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID</th>
                    <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Candidate Name</th>
                    <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Gender</th>
                    <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">State</th>
                    <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">WhatsApp</th>
                    <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCandidates.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 md:px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-[#4BB648] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                          </svg>
                          <p className="text-gray-500 text-lg">No candidates found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredCandidates.map((candidate) => (
                      <tr 
                        key={candidate.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={(e) => handleRowClick(candidate.id, e)}
                      >
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-black">{candidate.id}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-black">{candidate.candidate_name}</div>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            candidate.gender === 'female' 
                              ? 'bg-pink-100 text-pink-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {candidate.gender.charAt(0).toUpperCase() + candidate.gender.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-black">{candidate.state}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#4BB648] mr-2" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.545a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.545.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                            </svg>
                            <span className="text-sm text-black">{candidate.whatsapp}</span>
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleView(candidate.id);
                              }}
                              className="text-blue-600 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 p-2 rounded-full"
                              title="View Details"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(candidate.id);
                              }}
                              className="text-[#4BB648] hover:text-green-800 bg-green-100 hover:bg-green-200 p-2 rounded-full"
                              title="Edit Candidate"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(candidate.id);
                              }}
                              className="text-red-600 hover:text-red-900 bg-red-100 hover:bg-red-200 p-2 rounded-full"
                              title="Delete Candidate"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isViewModalOpen && selectedCandidate && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 z-50">
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
      
      {/* Header */}
      <div className="bg-[#4BB648] text-white p-2 rounded-t-lg flex justify-between items-center">
        <h2 className="text-lg font-bold">Candidate Details</h2>
        <button 
          onClick={() => setIsViewModalOpen(false)}
          className="text-white hover:text-gray-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Body */}
      <div className="p-2">
        <div className="space-y-2">
          
          {/* Profile */}
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-[#4BB648] flex items-center justify-center text-white text-xl font-bold mr-2">
              {selectedCandidate.candidate_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="text-lg font-bold text-black">{selectedCandidate.candidate_name}</h3>
              <p className="text-gray-600 text-sm">ID: {selectedCandidate.id}</p>
            </div>
          </div>
          
          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-500">Gender</p>
              <p className="font-medium text-black capitalize text-sm">{selectedCandidate.gender}</p>
            </div>
            
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-500">Age</p>
              <p className="font-medium text-black text-sm">{selectedCandidate.age}</p>
            </div>
            
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-500">State</p>
              <p className="font-medium text-black text-sm">{selectedCandidate.state}</p>
            </div>
            
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-500">Country</p>
              <p className="font-medium text-black text-sm">{selectedCandidate.country}</p>
            </div>
            
            <div className="bg-gray-50 p-2 rounded col-span-2">
              <p className="text-xs text-gray-500">Profession</p>
              <p className="font-medium text-black text-sm">{selectedCandidate.profession}</p>
            </div>
            
            <div className="bg-gray-50 p-2 rounded col-span-2">
              <p className="text-xs text-gray-500">WhatsApp</p>
              <p className="font-medium text-black flex items-center text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#4BB648] mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.545a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.545.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                {selectedCandidate.whatsapp}
              </p>
            </div>
            
            <div className="bg-gray-50 p-2 rounded col-span-2">
              <p className="text-xs text-gray-500">Additional Phone</p>
              <p className="font-medium text-black flex items-center text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#4BB648] mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.545a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.545.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                {selectedCandidate.additional_phone || 'Not provided'}
              </p>
            </div>
            
            <div className="bg-gray-50 p-2 rounded col-span-2">
              <p className="text-xs text-gray-500">Created At</p>
              <p className="font-medium text-black text-sm">{formatDate(selectedCandidate.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setIsViewModalOpen(false)}
            className="bg-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-400 transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
)}


        {isEditModalOpen && selectedCandidate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-[#4BB648] text-white p-4 rounded-t-lg flex justify-between items-center">
                <h2 className="text-xl font-bold">Edit Candidate</h2>
                <button 
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-white hover:text-gray-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleEditSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Name</label>
                    <input
                      type="text"
                      name="candidate_name"
                      value={editFormData.candidate_name || ''}
                      onChange={handleEditChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4BB648] focus:border-[#4BB648]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      name="gender"
                      value={editFormData.gender || ''}
                      onChange={handleEditChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4BB648] focus:border-[#4BB648]"
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                      <input
                        type="number"
                        name="age"
                        value={editFormData.age || ''}
                        onChange={handleEditChange}
                        min="18"
                        max="65"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4BB648] focus:border-[#4BB648]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <input
                        type="text"
                        name="country"
                        value={editFormData.country || ''}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4BB648] focus:border-[#4BB648]"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                    <input
                      type="text"
                      name="state"
                      value={editFormData.state || ''}
                      onChange={handleEditChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4BB648] focus:border-[#4BB648]"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                      <input
                        type="tel"
                        name="whatsapp"
                        value={editFormData.whatsapp || ''}
                        onChange={handleEditChange}
                        pattern="[0-9]{10}"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4BB648] focus:border-[#4BB648]"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Additional Phone</label>
                      <input
                        type="tel"
                        name="additional_phone"
                        value={editFormData.additional_phone || ''}
                        onChange={handleEditChange}
                        pattern="[0-9]{10}"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4BB648] focus:border-[#4BB648]"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Profession</label>
                    <input
                      type="text"
                      name="profession"
                      value={editFormData.profession || ''}
                      onChange={handleEditChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4BB648] focus:border-[#4BB648]"
                      required
                    />
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-[#4BB648] text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateTable;